import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CapitalizeFirstLetters,
  isValidAddress,
} from "../../helpers/utilities";
import { DiliTextCP } from "../Utility/DiliTextCP";
import { useHistory } from "react-router-dom";
import {
  getCitiesForState,
  getTimeCodesForCityAndStateAndZip,
} from "../../services/customerService";
import handleApiError from "../../helpers/handleApiError";
import { debounce } from "lodash";
import { Modal } from "react-bootstrap";
import { CDKOrderEditingRules } from "../../constants";

export const EditCDKCustomerModal = ({
  show,
  onHide,
  cdkOrder,
  finishUpdatingCustomerForOrder,
  handleUpdateTimeCodes,
}) => {
  // const generateInitialFormValues = () => {
  //   const cityStateZip = cdkOrder?.dataToSubmit?.customerAddress?.cityStateZip;
  //   const city = cityStateZip?.split(", ")?.[0];
  //   const stateAndZip = cityStateZip?.split(", ")?.[1];
  //   const state = stateAndZip?.split(" ")?.[0];
  //   const zip = stateAndZip?.split(" ")?.[1];
  //   return {
  //     customerName: cdkOrder?.dataToSubmit?.customerName ?? "",
  //     address: cdkOrder?.dataToSubmit?.customerAddress.address ?? "",
  //     city: city ? city : "",
  //     state: state ? state : "",
  //     zip: zip ? zip : "",
  //   };
  // };
  // console.log(cdkOrder?.dataToSubmit);
  const generateInitialFormValues = () => {
    return {
      customerName: cdkOrder?.dataToSubmit?.customerName ?? "",
      address: cdkOrder?.dataToSubmit?.customerAddress?.address ?? "",
      city:
        CapitalizeFirstLetters(cdkOrder?.dataToSubmit?.customerAddress?.city) ??
        "",
      state: cdkOrder?.dataToSubmit?.customerAddress?.state ?? "",
      zip: cdkOrder?.dataToSubmit?.customerAddress?.zip ?? "",
    };
  };
  const history = useHistory();
  const [formValues, setFormValues] = useState(generateInitialFormValues);
  const [fetchingCitiesForState, setFetchingCitiesForState] = useState(false);
  const [citiesForState, setCitiesForState] = useState([]);
  const [fetchedTimeCodes, setFetchedTimeCodes] = useState(
    cdkOrder?.dataToSubmit?.timeCodes ?? []
  );
  const [loadingTimeCodes, setLoadingTimeCodes] = useState(false);

  const citiesForStateOptions = useMemo(() => {
    return (
      <>
        <option value="">Select City</option>
        {citiesForState.map((cityForState) => {
          return (
            <option
              key={cityForState}
              value={CapitalizeFirstLetters(cityForState)}
            >
              {cityForState}
            </option>
          );
        })}
      </>
    );
  }, [citiesForState]);

  useEffect(() => {
    //when cdkOrder changes we need to update our values (i.e. we open the modal on a new row)
    setFormValues(generateInitialFormValues);
    setFetchedTimeCodes(cdkOrder?.dataToSubmit?.timeCodes ?? []); //initialize these to what the TimeCode component fetched, then we will refetch as form is changed

    //on modal load if a state is found, we need to grab cities we service that the client has access to
    const fetchData = async () => {
      if (cdkOrder?.dataToSubmit?.customerAddress?.state) {
        try {
          setFetchingCitiesForState(true);
          const citiesForStateResponse = await getCitiesForState(
            cdkOrder?.dataToSubmit?.customerAddress?.state
          );
          setCitiesForState(citiesForStateResponse.data.clientCity);
          // console.log(citiesForStateResponse);
        } catch (ex) {
          handleApiError(ex, history);
        } finally {
          setFetchingCitiesForState(false);
        }
      } else {
        setCitiesForState([]);
      }
    };

    fetchData();
  }, [cdkOrder]); //this is working fine even though it is a nested object?

  const fetchAndUpdateTimeCodesForCityStateAndZip = async (
    CDKOrderID,
    city,
    state,
    zip,
    signal
  ) => {
    try {
      //have to wrap this in a try catch as it can be cancelled
      const timeCodesResponse = await getTimeCodesForCityAndStateAndZip(
        city,
        state,
        zip,
        signal
      );
      // console.log(timeCodesResponse);
      // handleUpdateTimeCodes(CDKOrderID, timeCodesResponse.data);
      setFetchedTimeCodes(timeCodesResponse.data);
      // return timeCodesResponse;
    } catch (ex) {
      handleApiError(ex, history);
    } finally {
      setLoadingTimeCodes(false);
    }
  };
  const debouncedFetchAndUpdateTimeCodesForCityStateAndZip = useCallback(
    debounce(fetchAndUpdateTimeCodesForCityStateAndZip, 500),
    []
  );

  const fetchTimeCodesAbortControllerRef = useRef();

  const handleChangeForm = async (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => {
      return { ...prev, [name]: value };
    });
    if (
      e.target.name === "state" ||
      e.target.name === "city" ||
      e.target.name === "zip"
    ) {
      //always aborting on input as the following can happen:
      //user inputs 5 digits in zip, fetch is sent, user removes 1
      //so previous time codes will be removed and a new fetch will not be sent, but if we do not cancel the previous request, the previously fetched time codes will return and be added back
      //abort previous time code request if there is one
      fetchTimeCodesAbortControllerRef.current &&
        fetchTimeCodesAbortControllerRef.current.abort();
      fetchTimeCodesAbortControllerRef.current = new AbortController();
      setFetchedTimeCodes([]);
    }
    if (e.target.name === "state") {
      try {
        setFetchingCitiesForState(true);
        const citiesForStateResponse = await getCitiesForState(value);
        setCitiesForState(citiesForStateResponse.data.clientCity);
        // console.log(citiesForStateResponse);
      } catch (ex) {
        handleApiError(ex, history);
      } finally {
        setFetchingCitiesForState(false);
      }
    }
    if (
      (e.target.name === "city" &&
        e.target.value !== "" &&
        formValues.zip.length >= 5) ||
      (e.target.name === "zip" &&
        e.target.value.length >= 5 &&
        formValues.city !== "")
    ) {
      try {
        // //abort previous request if there is one
        // fetchTimeCodesAbortControllerRef.current &&
        //   fetchTimeCodesAbortControllerRef.current.abort();
        // fetchTimeCodesAbortControllerRef.current = new AbortController();
        setLoadingTimeCodes(true);
        debouncedFetchAndUpdateTimeCodesForCityStateAndZip(
          cdkOrder.CDKOrderID,
          e.target.name === "city" ? e.target.value : formValues.city,
          formValues.state,
          e.target.name === "zip"
            ? e.target.value.substring(0, 5)
            : formValues.zip.substring(0, 5),
          fetchTimeCodesAbortControllerRef.current.signal
        );

        // Fetch time codes
        // setFetchingCitiesForState(true);
        // const citiesForStateResponse = await getCitiesForState(value);
        // setCitiesForState(citiesForStateResponse.data.clientCity);
        // console.log(citiesForStateResponse);
      } catch (ex) {
        handleApiError(ex, history);
      } finally {
        // setFetchingCitiesForState(false);
      }
    }
  };

  // console.log(formValues);

  const [wasSubmitted, setWasSubmitted] = useState(false);

  const handleOnInvalid = () => {
    setWasSubmitted(true);
  };

  const handleSubmit = (e) => {
    // console.log("hi");
    e.preventDefault();
    handleUpdateTimeCodes(
      cdkOrder.CDKOrderID,
      cdkOrder.isMergedOrder,
      fetchedTimeCodes
    );
    finishUpdatingCustomerForOrder(
      cdkOrder.CDKOrderID,
      cdkOrder.isMergedOrder,
      formValues
    );
    onHide();
  };
  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Editing Customer</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit} onInvalid={handleOnInvalid}>
        <Modal.Body style={{ padding: 0 }}>
          <div className="row">
            {/* pr-0 must match breakpoint of col, idk why it is behaving so weirdly without the pr-0 (and pl-0 on the other col below) though, is this how bootstrap always was??? */}
            <div className="col-md-4 pr-md-0">
              <div
                className="cdk-customer-edit-subheader"
                style={{ borderRight: "1px solid #dee2e6" }}
              >
                CDK Data
              </div>
              <div
                style={{ overflow: "auto", borderRight: "1px solid #dee2e6" }}
              >
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    Customer Name
                  </div>
                  {cdkOrder?.originalCDKData?.customerName
                    ? cdkOrder.originalCDKData.customerName
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    Customer Address
                  </div>
                  {cdkOrder?.originalCDKData?.customerAddress
                    ? cdkOrder.originalCDKData.customerAddress
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    Customer CSZ
                  </div>
                  {/* // shipToLine1: cdkOrder.shipToLine1,
                // shipToLine2: cdkOrder.shipToLine2,
                // shipToLine3: cdkOrder.shipToLine3,
                // shipToLine4: cdkOrder.shipToLine4,
                // shipVia: cdkOrder.shipVia, */}
                  {cdkOrder?.originalCDKData?.customerCSZ
                    ? cdkOrder.originalCDKData.customerCSZ
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    shipToLine1
                  </div>
                  {cdkOrder?.originalCDKData?.shipToLine1
                    ? cdkOrder.originalCDKData.shipToLine1
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    shipToLine2
                  </div>
                  {cdkOrder?.originalCDKData?.shipToLine2
                    ? cdkOrder.originalCDKData.shipToLine2
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    shipToLine3
                  </div>
                  {cdkOrder?.originalCDKData?.shipToLine3
                    ? cdkOrder.originalCDKData.shipToLine3
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    shipToLine4
                  </div>
                  {cdkOrder?.originalCDKData?.shipToLine4
                    ? cdkOrder.originalCDKData.shipToLine4
                    : "Not Found"}
                </div>
                <div className="cdk-customer-edit-helper-display-container">
                  <div className="client-profile-info-display-header">
                    ShipVia
                  </div>
                  {cdkOrder?.originalCDKData?.shipVia
                    ? cdkOrder.originalCDKData.shipVia
                    : "Not Found"}
                </div>
              </div>
            </div>
            <div className="col-md-8 pl-md-0">
              <div className="cdk-customer-edit-subheader">Data to submit</div>
              <div className="form-row" style={{ padding: "1rem" }}>
                <DiliTextCP
                  required
                  className="col-12 form-group"
                  label="Customer Name"
                  name="customerName"
                  maxLength={CDKOrderEditingRules.customerName.maxLength}
                  value={formValues.customerName}
                  onChange={handleChangeForm}
                  wasSubmitted={wasSubmitted}
                />
                <DiliTextCP
                  required
                  className="col-12 form-group"
                  label="Customer Address"
                  name="address"
                  maxLength={CDKOrderEditingRules.customerAddress.maxLength}
                  value={formValues.address}
                  onChange={handleChangeForm}
                  wasSubmitted={wasSubmitted}
                />
                <DiliTextCP
                  required
                  type="select"
                  label="Customer State"
                  className="col-6 form-group"
                  name="state"
                  value={formValues.state}
                  onChange={handleChangeForm}
                  wasSubmitted={wasSubmitted}
                >
                  <option value="">Select State</option>
                  <option value="AL">AL</option>
                  <option value="AK">AK</option>
                  <option value="AZ">AZ</option>
                  <option value="AR">AR</option>
                  <option value="CA">CA</option>
                  <option value="CO">CO</option>
                  <option value="CT">CT</option>
                  <option value="DE">DE</option>
                  <option value="FL">FL</option>
                  <option value="GA">GA</option>
                  <option value="HI">HI</option>
                  <option value="ID">ID</option>
                  <option value="IL">IL</option>
                  <option value="IN">IN</option>
                  <option value="IA">IA</option>
                  <option value="KS">KS</option>
                  <option value="KY">KY</option>
                  <option value="LA">LA</option>
                  <option value="ME">ME</option>
                  <option value="MD">MD</option>
                  <option value="MA">MA</option>
                  <option value="MI">MI</option>
                  <option value="MN">MN</option>
                  <option value="MS">MS</option>
                  <option value="MO">MO</option>
                  <option value="MT">MT</option>
                  <option value="NE">NE</option>
                  <option value="NV">NV</option>
                  <option value="NH">NH</option>
                  <option value="NJ">NJ</option>
                  <option value="NM">NM</option>
                  <option value="NY">NY</option>
                  <option value="NC">NC</option>
                  <option value="ND">ND</option>
                  <option value="OH">OH</option>
                  <option value="OK">OK</option>
                  <option value="OR">OR</option>
                  <option value="PA">PA</option>
                  <option value="RI">RI</option>
                  <option value="SC">SC</option>
                  <option value="SD">SD</option>
                  <option value="TN">TN</option>
                  <option value="TX">TX</option>
                  <option value="UT">UT</option>
                  <option value="VT">VT</option>
                  <option value="VA">VA</option>
                  <option value="WA">WA</option>
                  <option value="WV">WV</option>
                  <option value="WI">WI</option>
                  <option value="WY">WY</option>
                </DiliTextCP>
                <DiliTextCP
                  required
                  type="select"
                  label="City"
                  name="city"
                  onChange={handleChangeForm}
                  value={formValues.city}
                  className="col-6 form-group"
                  wasSubmitted={wasSubmitted}
                  loading={fetchingCitiesForState}
                  showProblemText={
                    formValues.state &&
                    !fetchingCitiesForState &&
                    citiesForState.length <= 0
                  }
                  problemText={"No cities found for this state."}
                >
                  {citiesForStateOptions}
                </DiliTextCP>
                <DiliTextCP
                  required
                  label="Zip Code"
                  name="zip"
                  maxLength={CDKOrderEditingRules.customerZip.maxLength}
                  value={formValues.zip}
                  onChange={handleChangeForm}
                  wasSubmitted={wasSubmitted}
                  pattern="^[0-9]{5}(?:-[0-9]{4})?$"
                  helperText={"55555 ( -5555 )"}
                  showHelperText
                  className="col-6 form-group"
                />
                <div className="col-6"></div>
                <div className="col-6">
                  <div className="border rounded">
                    <div className="list-border-bottom p-2">
                      Available Time Codes
                    </div>

                    {loadingTimeCodes ? (
                      <div className="text-note p-2">Loading Time Codes</div>
                    ) : fetchedTimeCodes.length > 0 ? (
                      fetchedTimeCodes.map((timeCode) => {
                        return (
                          <div
                            key={timeCode}
                            className="list-border-bottom p-2"
                          >
                            {timeCode}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-danger p-2">
                        {isValidAddress(
                          formValues.city,
                          formValues.state,
                          formValues.zip
                        )
                          ? "No Time Codes found for this destination. Please contact us for more information."
                          : "Please make sure destination address is valid."}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            Cancel
          </button>
          <button
            disabled={loadingTimeCodes || fetchedTimeCodes.length === 0}
            type="submit"
            className="btn btn-primary"
          >
            Update
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
